import { Link } from "react-router-dom";
import { ArrowRight, CalendarPlus, Gift, Package, ShieldCheck, Activity, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/api";
import { DemoNotice, ErrorBox, Meter, Spinner, StatusBadge, useFetch } from "@/components/common";
import { DigitalCard } from "@/components/DigitalCard";

const QUICK = [
  { to: "/app/packages", label: "My Packages", icon: Package },
  { to: "/app/benefits", label: "Benefits", icon: Gift },
  { to: "/app/book", label: "Book Service", icon: CalendarPlus },
  { to: "/app/activity", label: "Activity", icon: Activity },
];

export function EntitlementRow({ e }) {
  const unlimited = e.included_qty == null;
  return (
    <div className="space-y-2" data-testid={`entitlement-${e.benefit_code}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold text-slate-800">{e.benefit_name}</span>
        <span className="font-mono text-xs font-semibold text-purple-700" data-testid={`entitlement-remaining-${e.benefit_code}`}>
          {unlimited ? "Unlimited" : `${e.remaining_qty} of ${e.included_qty} left`}
        </span>
      </div>
      {!unlimited && <Meter used={e.used_qty} total={e.included_qty} />}
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { data, error, loading } = useFetch("/customer/dashboard");
  if (loading) return <Spinner />;
  const cp = data?.packages?.[0];
  const ents = (data?.entitlements || []).filter((e) => e.customer_package_id === cp?.id && e.benefit_type !== "INSURANCE");
  return (
    <div className="space-y-6">
      <ErrorBox error={error} />
      <div className="rise">
        <p className="text-sm text-slate-500">Hello,</p>
        <h1 className="text-2xl font-extrabold tracking-tight" data-testid="customer-greeting">{user.full_name}</h1>
      </div>
      {cp ? (
        <div className="rise rise-1 space-y-3">
          <DigitalCard cp={cp} holder={user.full_name} />
          {cp.status === "PENDING_PAYMENT" && (
            <Link to="/app/packages" className="btn-primary w-full" data-testid="home-complete-activation">Complete activation <ArrowRight size={16} /></Link>
          )}
          {cp.status === "DEMO_ACTIVE" && <DemoNotice>This is a demo activation for testing. Benefits shown are not valid for real services.</DemoNotice>}
        </div>
      ) : (
        <Link to="/app/explore" className="surface flex items-center gap-3 p-4" data-testid="home-explore-cta"><Sparkles className="text-purple-600" /> Explore GIA CARE+ packages</Link>
      )}
      <div className="rise rise-2 grid grid-cols-4 gap-2">
        {QUICK.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} className="flex flex-col items-center gap-1.5 rounded-2xl p-2 text-center text-[11px] font-semibold text-slate-600 transition-colors hover:bg-purple-50" data-testid={`quick-${label.toLowerCase().replace(/ /g, "-")}`}>
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-50 to-blue-50 text-purple-700 ring-1 ring-purple-100"><Icon size={20} /></span>
            {label}
          </Link>
        ))}
      </div>
      {ents.length > 0 && (
        <section className="rise rise-3 surface space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold">Your benefits</h2>
            <Link to="/app/benefits" className="text-xs font-semibold text-purple-700" data-testid="home-view-benefits">View all</Link>
          </div>
          {ents.map((e) => <EntitlementRow key={e.id} e={e} />)}
        </section>
      )}
      {data?.insurance?.map((i) => (
        <div key={i.id} className="rise rise-3 surface flex items-center gap-3 p-4" data-testid="home-insurance-card">
          <ShieldCheck className="text-blue-600" />
          <div className="flex-1"><p className="text-sm font-semibold">{i.benefit_name}</p><p className="text-xs text-slate-500">Coverage is shown only after insurer confirmation</p></div>
          <StatusBadge status={i.status} />
        </div>
      ))}
      {data?.recent_requests?.length > 0 && (
        <section className="rise rise-4">
          <h2 className="mb-3 text-base font-bold">Recent requests</h2>
          <div className="space-y-2">
            {data.recent_requests.map((r) => (
              <div key={r.id} className="surface flex items-center justify-between p-3.5">
                <div><p className="text-sm font-semibold">{r.benefit_name}</p><p className="text-xs text-slate-500">{fmtDateTime(r.scheduled_at)} · {r.beneficiary_name}</p></div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
